import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogTitle, DialogContent, Accordion, AccordionSummary, AccordionDetails, Typography, makeStyles } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { APIEndpoints } from 'helpers/APILists';
import { Spinner, DarkBackground } from 'elements/Spinner/Spinner.styled';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useMsal } from '@azure/msal-react';
import { setAnnouncementState } from 'journeys/portal/Home/Home.actions';

const useStyles = makeStyles((theme) => ({
    accordion: {
        marginBottom: '8px',
        '&:last-child': {
            marginBottom: '8px',
            '&.Mui-expanded': {
                marginBottom: '8px',
            },
        },
        '&.Mui-expanded': {
            marginBottom: '8px',
            marginTop: '0px'
        },
    },
    date: {
        flex: '0 0 100px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '14px',
        fontWeight: 'bold',
        textAlign: 'center',
        backgroundColor: 'var(--primary)',
        color: 'white',
        marginRight: theme.spacing(2),
        height: '30px'
    },
    accordionHeader: {
        display: 'flex',
        alignItems: 'center',
        width: '100%'
    },
    featureName: {
        flex: 1,
        fontSize: '14px',
        overflowWrap: 'anywhere',
        lineHeight: '1.2'
    },
    description: {
        padding: '5px 0px 5px 5px',
        fontSize: '12px'
    },
    dialogPaper: {
        maxWidth: '500px'
    },
    accordionSummaryRoot: {
        minHeight: '30px',
        maxHeight: '30px',
        padding: '0px',
        '&.Mui-expanded': {
            minHeight: '30px'
        }
    },
    accordionSummaryContent: {
        margin: '0px',
        '&.Mui-expanded': {
            margin: '0px'
        }
    },
    title: {
        fontWeight: '600',
        padding: '8px 24px 0px 24px'
    },
    typographyH6: {
        fontWeight: '600'
    }
}));

const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' });
    return { day, month };
};

export const AnnouncementDialog = ({ open, onClose }) => {
    const classes = useStyles();
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const dispatch = useDispatch();
    const { instance } = useMsal();
    const userName = instance.getActiveAccount();
    const unreadAnnouncementsCount = useSelector(({ Home }) => Home.unreadAnnouncementsCount);

    useEffect(() => {
        const fetchAnnouncements = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await axios.get(APIEndpoints.ADMIN_GetAnnouncements);
                const { data } = response;
                const activeAnnouncements = data
                    .filter(a => a.Status === 'Active')
                    .sort((a, b) => {
                        const dateComparison = new Date(b.NewsDate) - new Date(a.NewsDate);
                        return dateComparison !== 0 ? dateComparison : new Date(b.CreatedDate) - new Date(a.CreatedDate);
                    })
                    .slice(0, 10);
                const maxId = activeAnnouncements.length > 0 ? Math.max(...activeAnnouncements.map(a => a.RowId)) : 0;
                setAnnouncements(activeAnnouncements);
                unreadAnnouncementsCount && dispatch(setAnnouncementState(userName.username, maxId));
            } catch (err) {
                setError('Failed to fetch announcements. Please try again later.'); // Set error message
            } finally {
                setLoading(false);
            }
        };

        if (open) {
            fetchAnnouncements();
        }
    }, [open]);

    if (loading) {
        return (
            <DarkBackground disappear={false}>
                <Spinner style={{ position: 'absolute', top: '50%', left: '50%' }} />
            </DarkBackground>
        );
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth classes={{ paper: classes.dialogPaper }}>
            <DialogTitle className={classes.title}>
                <Typography variant="h6" className={classes.typographyH6}>
                    Announcements
                </Typography>
            </DialogTitle>
            <DialogContent>
                {error && (
                    <Typography variant="body2" color="error">
                        {error}
                    </Typography>
                )}
                {!error && announcements.length > 0 ? (
                    announcements.map((announcement) => {
                        const id = announcement.RowId;
                        const { day, month } = formatDate(announcement.NewsDate);
                        const year = new Date(announcement.NewsDate).getFullYear();
                        return (
                            <Accordion key={id} className={classes.accordion}>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    aria-controls={`panel${id}-content`}
                                    id={`panel${id}-header`}
                                    classes={{
                                        root: classes.accordionSummaryRoot,
                                        content: classes.accordionSummaryContent,
                                    }}
                                >
                                    <div className={classes.accordionHeader}>
                                        <div className={classes.date}>{`${day} ${month} ${year}`}</div>
                                        <Typography className={classes.featureName} variant="body1">
                                            {announcement.FeatureName}
                                        </Typography>
                                    </div>
                                </AccordionSummary>
                                <AccordionDetails className={classes.description}>
                                    <Typography variant="body2" style={{ overflowWrap: 'anywhere' }}>
                                        {announcement.Announcement}
                                    </Typography>
                                </AccordionDetails>
                            </Accordion>
                        );
                    })
                ) : (
                    <Typography variant="body2" color="textSecondary">
                        No announcements available.
                    </Typography>
                )}
            </DialogContent>
        </Dialog>
    );
};

AnnouncementDialog.propTypes = {
    onClose: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired
};
